import { api } from "@/api/axios.ts";
import { ApiOperationResult } from "@/types/common.types.ts";
import {
  ApiEsignDocumentCounts,
  ApiEsignDocumentEnvelope,
  ApiEsignDocumentFilter,
  ApiEsignDocumentSummary,
  ApiEsignEnvelope,
  ApiEsignEnvelopeCreationJob,
  ApiEsignEnvelopeFilter,
  ApiEsignEnvelopePreview,
  ApiEsignEnvelopePreviewFilter,
  ApiEsignEnvelopeSave,
  ApiEsignEvidenceReport,
  ApiEsignMarkup,
  ApiEsignMarkupSave,
  ApiEsignOtpRequested,
  ApiEsignOtpVerify,
  ApiEsignRequest,
  ApiEsignRequestDecline,
  ApiEsignSignRequest,
  ApiEsignSigningContext,
} from "@/types/esign.types.ts";
import { ApiPagedData } from "@skailer-com/uikit/core";

export const apiEsign = {
  getMarkup: (documentId: number, signal?: AbortSignal) => {
    return api.get<ApiEsignMarkup | null>(
      `/api/v1/esign/documents/${documentId}/markup`,
      undefined,
      signal
    );
  },
  createMarkup: (documentId: number, payload: ApiEsignMarkupSave) => {
    return api.post<ApiEsignMarkup>(`/api/v1/esign/documents/${documentId}/markup`, payload);
  },
  updateMarkup: (documentId: number, payload: ApiEsignMarkupSave) => {
    return api.put<ApiEsignMarkup>(`/api/v1/esign/documents/${documentId}/markup`, payload);
  },
  getDocumentCounts: (signal?: AbortSignal) => {
    return api.get<ApiEsignDocumentCounts>(`/api/v1/esign/documents/counts`, undefined, signal);
  },
  searchDocuments: (payload: ApiEsignDocumentFilter, signal?: AbortSignal) => {
    return api.post<ApiPagedData<ApiEsignDocumentSummary>>(
      `/api/v1/esign/documents/search`,
      payload,
      signal
    );
  },
  searchEnvelopes: (payload: ApiEsignEnvelopeFilter, signal?: AbortSignal) => {
    return api.post<ApiPagedData<ApiEsignDocumentEnvelope>>(
      `/api/v1/esign/envelopes/search`,
      payload,
      signal
    );
  },
  createEnvelope: (payload: ApiEsignEnvelopeSave) => {
    return api.post<ApiEsignEnvelopeCreationJob>(`/api/v1/esign/envelopes`, payload);
  },
  previewEnvelope: (payload: ApiEsignEnvelopePreviewFilter, signal?: AbortSignal) => {
    return api.post<ApiEsignEnvelopePreview>(`/api/v1/esign/envelopes/preview`, payload, signal);
  },
  getEnvelope: (envelopeId: string, signal?: AbortSignal) => {
    return api.get<ApiEsignEnvelope>(`/api/v1/esign/envelopes/${envelopeId}`, undefined, signal);
  },
  cancelEnvelope: (envelopeId: string) => {
    return api.post<ApiEsignEnvelope>(`/api/v1/esign/envelopes/${envelopeId}/cancel`, {});
  },
  getEvidenceReport: (envelopeId: string, signal?: AbortSignal) => {
    return api.get<ApiEsignEvidenceReport>(
      `/api/v1/esign/envelopes/${envelopeId}/evidence-report`,
      undefined,
      signal
    );
  },
  getEvidenceReportPdf: (envelopeId: string) => {
    return api.get<BlobPart>(
      `/api/v1/esign/envelopes/${envelopeId}/evidence-report/pdf`,
      undefined,
      undefined,
      { responseType: "blob" }
    );
  },
  getRequest: (requestId: string, signal?: AbortSignal) => {
    return api.get<ApiEsignRequest>(`/api/v1/esign/requests/${requestId}`, undefined, signal);
  },
  getSigningContext: (requestId: string, signal?: AbortSignal) => {
    return api.get<ApiEsignSigningContext>(
      `/api/v1/esign/requests/${requestId}/signing-context`,
      undefined,
      signal
    );
  },
  getRequestSourceDocument: (requestId: string, signal?: AbortSignal) => {
    return api.get<BlobPart>(
      `/api/v1/esign/requests/${requestId}/document/source`,
      undefined,
      signal,
      { responseType: "blob" }
    );
  },
  getRequestSignedDocument: (requestId: string) => {
    return api.get<BlobPart>(
      `/api/v1/esign/requests/${requestId}/document/signed`,
      undefined,
      undefined,
      { responseType: "blob" }
    );
  },
  declineRequest: (requestId: string, payload: ApiEsignRequestDecline) => {
    return api.post<ApiEsignRequest>(`/api/v1/esign/requests/${requestId}/decline`, payload);
  },
  signRequest: (requestId: string, payload?: ApiEsignSignRequest) => {
    return api.post<ApiEsignRequest>(`/api/v1/esign/requests/${requestId}/sign`, payload ?? {});
  },
  requestOtp: () => {
    return api.post<ApiEsignOtpRequested>(`/api/v1/esign/otp/request`, {});
  },
  verifyOtp: (payload: ApiEsignOtpVerify) => {
    return api.post<ApiOperationResult>(`/api/v1/esign/otp/verify`, payload);
  },
};
